import { Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import DatePicker from 'components/date-picker';
import Dialog from 'components/dialog';
import FlexBox from 'components/flex-box';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';

import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TextField from 'components/text-field';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import dayjs from 'dayjs';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { Info } from 'lucide-react';
import Mui from 'materials';
import React, { useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { OwnerType } from 'types/shared/owner-type.enum';
import _, { some } from 'underscore';
import validateProfileOwner from 'utils/validate-profile-owner';
import { getProfilesAssortment } from 'views/planning-prognosis/api/get-profiles';

import Paper from 'components/paper';
import Switch from 'components/switch';
import { DefaultFishGroupProfileAssortment } from 'views/planning-prognosis/features/view-fish-group/types/default-fish-group-profile-assortment.type';
import { DefaultProfileType } from 'views/planning-prognosis/features/view-fish-group/types/default-profile-type.enum';
import { getFeedStrategy } from 'views/profiles-strategies/features/feed-strategies/api/get-feed-strategy';
import { deleteDefaultFishGroupProfile } from '../api/fish-group-planning/delete-default-fish-group-profile';
import { putFishGroupHarvestTarget } from '../api/fish-group-planning/put-fish-group-harvest-target';
import { putDefaultFishGroupProfiles } from '../api/fish-group-planning/update-default-fish-group-profiles';
import { postCalculation } from 'views/planning-prognosis/api/create-calculation';
import { AxiosResponse } from 'axios';

type ProfileValues = {
  type: DefaultProfileType;
  profileId: number;
};

type HarvestValues = {
  byWeight: boolean;
  date: string;
  weight: number;
};

type Props = {
  defaultProfile: DefaultFishGroupProfileAssortment;
  fishGroupId: string;
  globalSiteId: string;
  isEditable: boolean;
};

const FishGroupPlanning: React.FC<Props> = ({ defaultProfile, fishGroupId, globalSiteId, isEditable }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const { account } = useAccountState();
  const theme = useTheme();

  const resetDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const profilesDialogRef = useRef<React.ElementRef<typeof Dialog>[]>([]);

  const PAGE_NAME_ID = 'planningPrognosis.viewFishGroup';

  // #region STATE

  const [profileSearchValue, setProfileSearchValue] = useState<string>('');

  // #endregion STATE

  // #region FORMS

  const { handleSubmit: handleSubmitHarvest, control: controlHarvest } = useForm<HarvestValues>({
    defaultValues: useMemo(() => {
      return {
        byWeight: defaultProfile?.harvestTarget?.harvestByWeight ?? false,
        date: !defaultProfile?.harvestTarget?.harvestByWeight
          ? defaultProfile?.harvestTarget?.harvestTargetDate
          : dayjs().utc(true).toISOString(),
        weight: defaultProfile?.harvestTarget?.harvestTargetWeight ?? 0,
      };
    }, [defaultProfile]),
    shouldUnregister: false,
  });

  const harvestByWeight = useWatch({
    control: controlHarvest,
    name: 'byWeight',
  });

  const {
    handleSubmit: handleSubmitProfile,
    control: controlProfile,
    reset: resetProfile,
    watch: watchProfile,
  } = useForm<ProfileValues>({
    defaultValues: {
      type: DefaultProfileType.EFI,
      profileId: 0,
    },
    shouldUnregister: false,
  });

  // #endregion FORMS

  // #region FETCH DATA

  const { data: profiles } = useGet(
    ['getProfilesAssortment', account.company?.marketId, account.company?.companyId],
    ({ queryKey }) => getProfilesAssortment(Number(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!account.company?.marketId && !!account.company?.companyId,
    }
  );

  const { data: feedStrategy } = useGet(
    ['getFeedStrategy', defaultProfile.feedStrategyProfile?.profileId],
    ({ queryKey }) => getFeedStrategy(String(queryKey[1])),
    {
      enabled: !!defaultProfile.feedStrategyProfile?.profileId,
    }
  );
  // #endregion FETCH DATA

  // #region POST DATA

  const startCalculation = usePost<AxiosResponse, { owner: OwnerType; id: string }>(
    (request) => postCalculation(request.owner, request.id),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getFishGroupCalculationStatus'],
      });
    }
  );

  const updateHarvestTarget = usePost<number, { index: number; request: { date: string; weight: number } }>(
    (request) => putFishGroupHarvestTarget(fishGroupId, request.request, request.index),
    (response) => {
      [['getFishGroup'], ['getDefaultFishGroupProfiles'], ['getFishGroupCalculationStatus']].forEach((queryKey) => {
        queryClient.invalidateQueries({
          queryKey: queryKey,
        });
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      if (!!response) profilesDialogRef.current[response].setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const updateDefaultProfile = usePost<{ index: number; type: DefaultProfileType }, { index: number; request: ProfileValues }>(
    (request) => putDefaultFishGroupProfiles([fishGroupId], request.request.type, request.request.profileId, request.index),
    (response) => {
      queryClient.invalidateQueries({
        queryKey: ['getFishGroup'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getDefaultFishGroupProfiles'],
      });
      startCalculation.mutate({ owner: OwnerType.FISH_GROUP, id: fishGroupId });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      if (!!response) profilesDialogRef.current[response.index].setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const deleteDefaultProfile = usePost(
    () => deleteDefaultFishGroupProfile(OwnerType.FISH_GROUP, fishGroupId),
    () => {
      [['getFishGroup'], ['getDefaultFishGroupProfiles'], ['getFishGroupCalculationStatus']].forEach((queryKey) => {
        queryClient.invalidateQueries({
          queryKey: queryKey,
        });
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      resetDialogRef.current?.setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // #endregion POST DATA

  // #region HANDLERS
  const handleUpdateHarvestTarget = (index: number, request: HarvestValues) => {
    if (!!defaultProfile && !!fishGroupId) {
      updateHarvestTarget.mutate({
        index: index,
        request: {
          date: request.date,
          weight: request.byWeight ? request.weight : 0,
        },
      });
    }
  };

  const handleUpdateDefaultProfile = (index: number, request: ProfileValues) => {
    if (!!defaultProfile && !!fishGroupId) {
      updateDefaultProfile.mutate({
        index: index,
        request: request,
      });
    }
  };

  const handleDeleteProfiles = () => {
    deleteDefaultProfile.mutate({});
  };

  // #endregion HANDLERS

  return (
    <>
      <Paper transparent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={5}>
          <Typography variant="h3" color={theme.palette.common.white}>
            <FormattedMessage id={`${PAGE_NAME_ID}.profileList.title`} />
          </Typography>
          <Dialog
            ref={resetDialogRef}
            maxWidth="xs"
            title={intl.formatMessage({ id: 'dialog.delete.title' })}
            button={
              <Button disabled={!isEditable} sx={{ mb: { xs: 3, md: 0 } }}>
                <FormattedMessage id={`${PAGE_NAME_ID}.profileList.resetDefaultsBtn`} />
              </Button>
            }>
            <Box display={'flex'} justifyContent={'flex-end'}>
              <Button type="submit" onClick={handleDeleteProfiles}>
                <FormattedMessage id={`general.reset`} />
              </Button>
            </Box>
          </Dialog>
        </Box>

        <List
          sx={{ mt: 5 }}
          options={[
            { id: 'type', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.profileList.profileLabel` }) },
            { id: 'name', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.profileList.valueLabel` }) },
            { id: 'level', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.profileList.levelLabel` }) },
            { id: 'actions', label: '', align: 'right', autoWidth: true },
          ]}>
          <ListRow>
            <ListCell sx={{ whiteSpace: 'nowrap' }}>
              <FormattedMessage id={`${PAGE_NAME_ID}.profileList.harvestTarget`} />
            </ListCell>
            <ListCell>
              {defaultProfile?.harvestTarget?.harvestByWeight ? (
                <FormattedNumber
                  value={defaultProfile?.harvestTarget?.harvestTargetWeight}
                  style={`unit`}
                  unit="gram"
                  minimumFractionDigits={0}
                  maximumFractionDigits={0}
                />
              ) : (
                <FormattedDate value={defaultProfile?.harvestTarget?.harvestTargetDate} day="2-digit" month="short" year="numeric" />
              )}
            </ListCell>
            <ListCell>
              <FormattedMessage
                id={
                  defaultProfile?.harvestTarget
                    ? `${PAGE_NAME_ID}.profileList.profileLevel${
                        String(defaultProfile?.harvestTarget?.profileLevel ?? '')
                          .charAt(0)
                          .toUpperCase() + String(defaultProfile?.harvestTarget?.profileLevel ?? '').slice(1)
                      }`
                    : `${PAGE_NAME_ID}.profileList.emptyLevel`
                }
              />
            </ListCell>
            <ListCell sx={{ width: { xs: '100%', md: 'auto' } }} align="right">
              <Dialog
                ref={(el) => {
                  if (el) profilesDialogRef.current[0] = el;
                }}
                title={intl.formatMessage({ id: `${PAGE_NAME_ID}.profilesDialog.title` }, { type: 'Harvest Target' })}
                maxWidth="xs"
                button={
                  <Button
                    size="small"
                    disabled={!isEditable}
                    title={intl.formatMessage({ id: `${PAGE_NAME_ID}.profilesDialog.title` }, { type: 'Harvest Target' })}>
                    <FormattedMessage id="general.edit" values={{ type: '' }} />
                  </Button>
                }>
                <form onSubmit={handleSubmitHarvest((data) => handleUpdateHarvestTarget(0, data))}>
                  {updateDefaultProfile.isPending && <LoadingSpinner overlay />}
                  <FlexBox gap={4}>
                    <Switch
                      dark
                      size="medium"
                      label={
                        harvestByWeight
                          ? intl.formatMessage({ id: `${PAGE_NAME_ID}.profilesDialog.byWeightLabel` })
                          : intl.formatMessage({ id: `${PAGE_NAME_ID}.profilesDialog.dateBtn` })
                      }
                      controller={{
                        control: controlHarvest,
                        name: 'byWeight',
                      }}
                    />
                    {harvestByWeight ? (
                      <TextField
                        dark
                        fullWidth
                        type="number"
                        textAlign="right"
                        label={intl.formatMessage({ id: `${PAGE_NAME_ID}.profilesDialog.weightLabel` })}
                        controller={{
                          control: controlHarvest,
                          name: 'weight',
                        }}
                        endAdornment={'g'}
                      />
                    ) : (
                      <DatePicker
                        dark
                        fullWidth
                        label={intl.formatMessage({ id: `${PAGE_NAME_ID}.profilesDialog.dateLabel` })}
                        controller={{
                          control: controlHarvest,
                          name: 'date',
                        }}
                      />
                    )}

                    <Box display={'flex'} justifyContent={'end'}>
                      <Button type="submit">
                        <FormattedMessage id="general.update" />
                      </Button>
                    </Box>
                  </FlexBox>
                </form>
              </Dialog>
            </ListCell>
          </ListRow>
          <ListRow>
            <ListCell sx={{ whiteSpace: 'nowrap' }}>
              <FormattedMessage id={`${PAGE_NAME_ID}.profileList.egi`} />
            </ListCell>
            <ListCell>{defaultProfile?.egi?.profileName}</ListCell>
            <ListCell>
              <FormattedMessage
                id={
                  defaultProfile?.egi?.profileLevel
                    ? `${PAGE_NAME_ID}.profileList.profileLevel${
                        String(defaultProfile?.egi?.profileLevel ?? '')
                          .charAt(0)
                          .toUpperCase() + String(defaultProfile?.egi?.profileLevel ?? '').slice(1)
                      }`
                    : `${PAGE_NAME_ID}.profileList.emptyLevel`
                }
              />
            </ListCell>
            <ListCell sx={{ width: { xs: '100%', md: 'auto' } }} align="right">
              <Dialog
                ref={(el) => {
                  if (el) profilesDialogRef.current[1] = el;
                }}
                title={intl.formatMessage({ id: `${PAGE_NAME_ID}.profilesDialog.title` }, { type: 'Egi' })}
                maxWidth="xs"
                button={
                  <Button
                    size="small"
                    disabled={!isEditable}
                    onClick={() =>
                      resetProfile({
                        profileId: defaultProfile.egi?.profileId,
                        type: DefaultProfileType.EGI,
                      })
                    }
                    title={intl.formatMessage({ id: `${PAGE_NAME_ID}.profilesDialog.title` }, { type: 'Harvest Target' })}>
                    <FormattedMessage id="general.edit" values={{ type: '' }} />
                  </Button>
                }>
                <form onSubmit={handleSubmitProfile((data) => handleUpdateDefaultProfile(1, data))}>
                  {updateDefaultProfile.isPending && <LoadingSpinner overlay />}
                  <FlexBox>
                    <Select
                      dark
                      fullWidth
                      label={intl.formatMessage({ id: `${PAGE_NAME_ID}.profilesDialog.egiLabel` })}
                      controller={{
                        control: controlProfile,
                        name: `profileId`,
                      }}>
                      {!!profiles?.egi &&
                        _.chain(profiles.egi)
                          .filter((profile) =>
                            validateProfileOwner(profile, account.company?.marketId, String(account.company?.companyId), globalSiteId)
                          )
                          .sortBy((p) => p.name.toLowerCase())
                          .value()
                          .map((row) => (
                            <RadioSelectItem key={row.id} value={row.id} selected={row.id === watchProfile('profileId')}>
                              {row.name}
                            </RadioSelectItem>
                          ))}
                    </Select>

                    <Box display={'flex'} justifyContent={'end'}>
                      <Button type="submit">
                        <FormattedMessage id="general.update" />
                      </Button>
                    </Box>
                  </FlexBox>
                </form>
              </Dialog>
            </ListCell>
          </ListRow>
          <ListRow>
            <ListCell sx={{ whiteSpace: 'nowrap' }}>
              <FormattedMessage id={`${PAGE_NAME_ID}.profileList.efi`} />
            </ListCell>
            <ListCell>{defaultProfile?.efi?.profileName}</ListCell>
            <ListCell>
              <FormattedMessage
                id={
                  defaultProfile?.efi
                    ? `${PAGE_NAME_ID}.profileList.profileLevel${
                        String(defaultProfile?.efi?.profileLevel ?? '')
                          .charAt(0)
                          .toUpperCase() + String(defaultProfile?.efi?.profileLevel ?? '').slice(1)
                      }`
                    : `${PAGE_NAME_ID}.profileList.emptyLevel`
                }
              />
            </ListCell>
            <ListCell sx={{ width: { xs: '100%', md: 'auto' } }} align="right">
              <Dialog
                ref={(el) => {
                  if (el) profilesDialogRef.current[2] = el;
                }}
                title={intl.formatMessage({ id: `${PAGE_NAME_ID}.profilesDialog.title` }, { type: 'Efi' })}
                maxWidth="xs"
                button={
                  <Button
                    size="small"
                    disabled={!isEditable}
                    onClick={() => {
                      resetProfile({
                        profileId: defaultProfile.efi?.profileId,
                        type: DefaultProfileType.EFI,
                      });
                    }}
                    title={intl.formatMessage({ id: `${PAGE_NAME_ID}.profilesDialog.title` }, { type: 'Harvest Target' })}>
                    <FormattedMessage id="general.edit" values={{ type: '' }} />
                  </Button>
                }>
                <form onSubmit={handleSubmitProfile((data) => handleUpdateDefaultProfile(2, data))}>
                  {updateDefaultProfile.isPending && <LoadingSpinner overlay />}
                  <FlexBox>
                    <Select
                      dark
                      fullWidth
                      label={intl.formatMessage({ id: `${PAGE_NAME_ID}.profilesDialog.efiLabel` })}
                      controller={{
                        control: controlProfile,
                        name: `profileId`,
                      }}>
                      {!!profiles?.efi &&
                        _.chain(profiles.efi)
                          .filter((profile) =>
                            validateProfileOwner(profile, account.company?.marketId, String(account.company?.companyId), globalSiteId)
                          )
                          .sortBy((p) => p.name.toLowerCase())
                          .value()
                          .map((row) => (
                            <RadioSelectItem key={row.id} value={row.id} selected={row.id === watchProfile('profileId')}>
                              {row.name}
                            </RadioSelectItem>
                          ))}
                    </Select>

                    <Box display={'flex'} justifyContent={'end'}>
                      <Button type="submit">
                        <FormattedMessage id="general.update" />
                      </Button>
                    </Box>
                  </FlexBox>
                </form>
              </Dialog>
            </ListCell>
          </ListRow>
          <ListRow>
            <ListCell sx={{ whiteSpace: 'nowrap' }}>
              <FormattedMessage id={`${PAGE_NAME_ID}.profileList.mortality`} />
            </ListCell>
            <ListCell>{defaultProfile?.mortality?.profileName}</ListCell>
            <ListCell>
              <FormattedMessage
                id={
                  defaultProfile?.mortality
                    ? `${PAGE_NAME_ID}.profileList.profileLevel${
                        String(defaultProfile?.mortality?.profileLevel ?? '')
                          .charAt(0)
                          .toUpperCase() + String(defaultProfile?.mortality?.profileLevel ?? '').slice(1)
                      }`
                    : `${PAGE_NAME_ID}.profileList.emptyLevel`
                }
              />
            </ListCell>
            <ListCell sx={{ width: { xs: '100%', md: 'auto' } }} align="right">
              <Dialog
                ref={(el) => {
                  if (el) profilesDialogRef.current[3] = el;
                }}
                title={intl.formatMessage({ id: `${PAGE_NAME_ID}.profilesDialog.title` }, { type: 'Mortality' })}
                maxWidth="xs"
                button={
                  <Button
                    size="small"
                    disabled={!isEditable}
                    onClick={() =>
                      resetProfile({
                        profileId: defaultProfile.mortality?.profileId,
                        type: DefaultProfileType.MORTALITY,
                      })
                    }
                    title={intl.formatMessage({ id: `${PAGE_NAME_ID}.profilesDialog.title` }, { type: 'Harvest Target' })}>
                    <FormattedMessage id="general.edit" values={{ type: '' }} />
                  </Button>
                }>
                <form onSubmit={handleSubmitProfile((data) => handleUpdateDefaultProfile(3, data))}>
                  {updateDefaultProfile.isPending && <LoadingSpinner overlay />}
                  <FlexBox>
                    <Select
                      dark
                      fullWidth
                      label={intl.formatMessage({ id: `${PAGE_NAME_ID}.profilesDialog.mortalityLabel` })}
                      controller={{
                        control: controlProfile,
                        name: `profileId`,
                      }}>
                      {!!profiles?.mort &&
                        _.chain(profiles.mort)
                          .filter((profile) =>
                            validateProfileOwner(profile, account.company?.marketId, String(account.company?.companyId), globalSiteId)
                          )
                          .sortBy((p) => p.name.toLowerCase())
                          .value()
                          .map((row) => (
                            <RadioSelectItem key={row.id} value={row.id} selected={row.id === watchProfile('profileId')}>
                              {row.name}
                            </RadioSelectItem>
                          ))}
                    </Select>
                    <Mui.FormControl fullWidth>
                      <Box display={'flex'} justifyContent={'end'}>
                        <Button type="submit">
                          <FormattedMessage id="general.update" />
                        </Button>
                      </Box>
                    </Mui.FormControl>
                  </FlexBox>
                </form>
              </Dialog>
            </ListCell>
          </ListRow>
          <ListRow>
            <ListCell sx={{ whiteSpace: 'nowrap' }}>
              <FormattedMessage id={`${PAGE_NAME_ID}.profileList.feedStrategy`} />
            </ListCell>
            <ListCell>{defaultProfile?.feedStrategyProfile?.profileName}</ListCell>
            <ListCell>
              <FormattedMessage
                id={
                  defaultProfile?.feedStrategyProfile
                    ? `${PAGE_NAME_ID}.profileList.profileLevel${
                        String(defaultProfile?.feedStrategyProfile?.profileLevel ?? '')
                          .charAt(0)
                          .toUpperCase() + String(defaultProfile?.feedStrategyProfile?.profileLevel ?? '').slice(1)
                      }`
                    : `${PAGE_NAME_ID}.profileList.emptyLevel`
                }
              />
            </ListCell>
            <ListCell sx={{ width: { xs: '100%', md: 'auto' } }} align="right">
              <Box display="flex" justifyContent={{ xs: 'space-between', md: 'flex-end' }} alignItems="center" gap={2} width={'100%'}>
                <Dialog
                  title={intl.formatMessage({ id: `${PAGE_NAME_ID}.feedStrategyList.title` })}
                  maxWidth="xs"
                  button={
                    <IconButton
                      onClick={() =>
                        resetProfile({
                          profileId: defaultProfile.feedStrategyProfile?.profileId,
                          type: DefaultProfileType.FEED_STRATEGY,
                        })
                      }
                      disabled={!isEditable}
                      title={intl.formatMessage({ id: `${PAGE_NAME_ID}.feedStrategyList.title` })}>
                      <Info size={18} />
                    </IconButton>
                  }>
                  <Box>
                    <List
                      dark
                      options={[
                        { id: 'fromWeight', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.feedStrategyList.fromWeightLabel` }) },
                        { id: 'name', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.feedStrategyList.nameLabel` }) },
                      ]}>
                      {feedStrategy?.lines.map((fs, idx) => (
                        <ListRow key={idx}>
                          <ListCell>{fs.fromWeight}</ListCell>
                          <ListCell>{fs.product?.name}</ListCell>
                        </ListRow>
                      ))}
                    </List>
                    <Box display={'flex'} justifyContent={'end'}>
                      <Button component={Link} to={`/profiles-strategies/feed-strategies/${feedStrategy?.id}`}>
                        <FormattedMessage id={`${PAGE_NAME_ID}.feedStrategyList.editBtn`} />
                      </Button>
                    </Box>
                  </Box>
                </Dialog>
                <Dialog
                  ref={(el) => {
                    if (el) profilesDialogRef.current[4] = el;
                  }}
                  title={intl.formatMessage({ id: `${PAGE_NAME_ID}.profilesDialog.title` }, { type: 'Feed Strategy' })}
                  maxWidth="xs"
                  button={
                    <Button
                      size="small"
                      disabled={!isEditable}
                      title={intl.formatMessage({ id: `${PAGE_NAME_ID}.profilesDialog.title` }, { type: 'Feed Strategy' })}
                      onClick={() =>
                        resetProfile({
                          type: DefaultProfileType.FEED_STRATEGY,
                          profileId: defaultProfile?.feedStrategyProfile?.profileId,
                        })
                      }>
                      <FormattedMessage id="general.edit" values={{ type: '' }} />
                    </Button>
                  }>
                  <form onSubmit={handleSubmitProfile((data) => handleUpdateDefaultProfile(4, data))}>
                    {updateDefaultProfile.isPending && <LoadingSpinner overlay />}
                    <FlexBox>
                      <Select
                        fullWidth
                        search={{ value: profileSearchValue, onChange: setProfileSearchValue }}
                        dark
                        label={intl.formatMessage({ id: `${PAGE_NAME_ID}.profilesDialog.feedStrategyLabel` })}
                        controller={{
                          control: controlProfile,
                          name: `profileId`,
                        }}>
                        {!!profiles?.feed &&
                          _.chain(profiles.feed)
                            .filter(
                              (profile) =>
                                profile.active &&
                                profile.name.toLowerCase().includes(profileSearchValue.toLowerCase()) &&
                                validateProfileOwner(
                                  profile,
                                  account.company?.marketId,
                                  String(account.company?.companyId),
                                  globalSiteId
                                )
                            )
                            .sortBy((p) => p.name.toLowerCase())
                            .value()

                            .map((row) => (
                              <RadioSelectItem key={row.id} value={row.id} selected={row.id === watchProfile('profileId')}>
                                {row.name}
                              </RadioSelectItem>
                            ))}
                      </Select>

                      <Box display={'flex'} justifyContent={'end'}>
                        <Button type="submit">
                          <FormattedMessage id="general.update" />
                        </Button>
                      </Box>
                    </FlexBox>
                  </form>
                </Dialog>
              </Box>
            </ListCell>
          </ListRow>
          <ListRow>
            <ListCell>
              <FormattedMessage id={`${PAGE_NAME_ID}.profileList.temperature`} />
            </ListCell>
            <ListCell>
              <span style={{ whiteSpace: 'break-spaces' }}>{defaultProfile?.temperatureProfile?.profileName} </span>
            </ListCell>
            <ListCell>
              {!!defaultProfile?.temperatureProfile?.profileLevel ? (
                <FormattedMessage
                  id={
                    defaultProfile?.temperatureProfile
                      ? `${PAGE_NAME_ID}.profileList.profileLevel${
                          String(defaultProfile?.temperatureProfile?.profileLevel ?? '')
                            .charAt(0)
                            .toUpperCase() + String(defaultProfile?.temperatureProfile?.profileLevel ?? '').slice(1)
                        }`
                      : `${PAGE_NAME_ID}.profileList.emptyLevel`
                  }
                />
              ) : (
                <FormattedMessage id="general.notApplicable" />
              )}
            </ListCell>
            <ListCell></ListCell>
          </ListRow>
        </List>
      </Paper>
    </>
  );
};

export default FishGroupPlanning;
